import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser, signOut, fetchAuthSession, signIn } from 'aws-amplify/auth';

export type UserRole = 'superadmin' | 'admin' | 'user';

interface AppConfig {
  apiUrl: string;
}

function getApiUrl(): string {
  if (isLocalDev()) {
    return 'http://localhost:3001';
  }
  const config = (window as unknown as { appConfig: AppConfig }).appConfig;
  return config?.apiUrl || 'http://localhost:3001';
}

export interface User {
  userId: string;
  email: string;
  name?: string;
  role: UserRole;
}

// Helper functions for role checks
export function isSuperAdmin(user: User | null): boolean {
  return user?.role === 'superadmin';
}

export function isAdmin(user: User | null): boolean {
  return user?.role === 'admin';
}

export function isGlobalAdmin(user: User | null): boolean {
  return user?.role === 'superadmin' || user?.role === 'admin';
}

// Mock users for local development matching the permission model
export const DEV_USERS: Record<string, User> = {
  // Global roles
  superAdmin: {
    userId: 'dev-superadmin',
    email: 'superuser@concept.de',
    name: 'Super Admin',
    role: 'superadmin',
  },
  admin: {
    userId: 'dev-admin',
    email: 'admin@concept.de',
    name: 'Global Admin',
    role: 'admin',
  },
  // Project A users
  projectAAdmin: {
    userId: 'dev-project-a-admin',
    email: 'project_a_admin@concept.de',
    name: 'Project A Admin',
    role: 'user',
  },
  projectAEditor: {
    userId: 'dev-project-a-editor',
    email: 'project_a_editor@concept.de',
    name: 'Project A Editor',
    role: 'user',
  },
  projectAViewer: {
    userId: 'dev-project-a-viewer',
    email: 'project_a_viewer@concept.de',
    name: 'Project A Viewer',
    role: 'user',
  },
  // Project B users
  projectBAdmin: {
    userId: 'dev-project-b-admin',
    email: 'project_b_admin@concept.de',
    name: 'Project B Admin',
    role: 'user',
  },
  projectBEditor: {
    userId: 'dev-project-b-editor',
    email: 'project_b_editor@concept.de',
    name: 'Project B Editor',
    role: 'user',
  },
  projectBViewer: {
    userId: 'dev-project-b-viewer',
    email: 'project_b_viewer@concept.de',
    name: 'Project B Viewer',
    role: 'user',
  },
  // Team-specific users
  teamAViewer: {
    userId: 'dev-team-a-viewer',
    email: 'project_a_team_a_viewer@concept.de',
    name: 'Team A Viewer',
    role: 'user',
  },
  teamAEditor: {
    userId: 'dev-team-a-editor',
    email: 'project_a_team_a_editor@concept.de',
    name: 'Team A Editor',
    role: 'user',
  },
  teamAAdmin: {
    userId: 'dev-team-a-admin',
    email: 'project_a_team_a_admin@concept.de',
    name: 'Team A Admin',
    role: 'user',
  },
};

const LOCAL_DEV_USER_KEY = 'local-dev-user-role';
const LOCAL_DEV_LOGOUT_KEY = 'local-dev-logged-out';

export function isLocalDev(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_USER_POOL_ID;
}

function getStoredDevUserRole(): string {
  return localStorage.getItem(LOCAL_DEV_USER_KEY) || 'superAdmin';
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkUser = useCallback(async () => {
    // Local development mode - bypass auth
    if (isLocalDev()) {
      // Check if user has logged out
      if (sessionStorage.getItem(LOCAL_DEV_LOGOUT_KEY)) {
        console.log('🔓 Local dev mode: User logged out');
        setUser(null);
        setIsLoading(false);
        return;
      }
      const role = getStoredDevUserRole();
      const devUser = DEV_USERS[role] || DEV_USERS.superAdmin;
      console.log(`🔓 Local dev mode: Using mock user (${role})`);
      setUser(devUser);
      setIsLoading(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (idToken) {
        // Fetch user details from API to get the role from DynamoDB
        const response = await fetch(`${getApiUrl()}/api/me`, {
          headers: {
            Authorization: `Bearer ${idToken.toString()}`,
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser({
            userId: userData.userId,
            email: userData.email,
            name: userData.name,
            role: userData.role || 'user',
          });
        } else {
          // Fallback to token data if API fails
          const currentUser = await getCurrentUser();
          setUser({
            userId: currentUser.userId,
            email: idToken.payload.email as string,
            name: idToken.payload.name as string,
            role: 'user',
          });
        }
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  async function login(email: string, password: string) {
    // Local development mode - auto login
    if (isLocalDev()) {
      sessionStorage.removeItem(LOCAL_DEV_LOGOUT_KEY);
      const role = getStoredDevUserRole();
      setUser(DEV_USERS[role] || DEV_USERS.superAdmin);
      return;
    }

    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        await checkUser();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw new Error('Password change required. Please contact your administrator.');
      } else {
        throw new Error('Sign in failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async function logout() {
    // Local development mode
    if (isLocalDev()) {
      sessionStorage.setItem(LOCAL_DEV_LOGOUT_KEY, 'true');
      setUser(null);
      return;
    }

    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Switch dev user role (only works in local dev mode)
  function setDevUserRole(role: string) {
    if (!isLocalDev()) {
      console.warn('setDevUserRole only works in local dev mode');
      return;
    }

    const devUser = DEV_USERS[role];
    if (!devUser) {
      console.warn(`Unknown dev user role: ${role}`);
      return;
    }

    localStorage.setItem(LOCAL_DEV_USER_KEY, role);
    sessionStorage.removeItem(LOCAL_DEV_LOGOUT_KEY);
    setUser(devUser);
    console.log(`🔓 Switched to dev user: ${role}`);
  }

  return {
    user,
    isLoading,
    login,
    logout,
    checkUser,
    // Dev mode helpers
    setDevUserRole,
    isLocalDev: isLocalDev(),
    currentDevRole: isLocalDev() ? getStoredDevUserRole() : null,
  };
}
